import type { Incident, ObjectId } from '@/api/types'

/**
 * Orden de prioridad del listado: `extensions.c_incidentactive.priority`,
 * propiedades `data_priority`. Ids entregados por negocio el 28-sep-2026; en
 * Esmax los ids son los mismos en staging y producción.
 *
 * Se compara por id y no por nombre, que es lo que se puede renombrar. Si un
 * ambiente trae otros ids, el orden se degrada a "solo por fecha" — por eso
 * `priorityRank` avisa en consola en vez de callarse.
 */
export const PRIORITY_ORDER: readonly ObjectId[] = [
  '61ef10a1785d1740bc45df40', // Alta
  '62632416ede5180007e1f03c', // Media Alta
  '61ef10a1785d178c0645df3f', // Media
  '61ef10a1785d177af945df43', // Baja
]

export const NO_AREA_KEY = '__none__'
export const NO_AREA_LABEL = 'Sin clasificar'

export interface IncidentGroup {
  /** `status2` de las incidencias del grupo, o `NO_AREA_KEY`. Estable entre recargas. */
  key: string
  label: string
  incidents: Incident[]
}

const warnedPriorities = new Set<ObjectId>()

/**
 * Posición de la prioridad en `PRIORITY_ORDER`. Sin prioridad, o con una que
 * no está en la lista, va después de Baja: se muestra igual, al final.
 */
function priorityRank(incident: Incident): number {
  const id = incident.priority._id
  const rank = id ? PRIORITY_ORDER.indexOf(id) : -1
  if (rank >= 0) return rank

  if (id && !warnedPriorities.has(id)) {
    warnedPriorities.add(id)
    console.warn(
      `[portal] prioridad desconocida ${id} (${incident.priority.display}): ` +
        'se ordena al final. Si son todas, los ids de PRIORITY_ORDER no son los de este ambiente.',
    )
  }
  return PRIORITY_ORDER.length
}

const time = (iso: string | null): number => {
  const t = iso ? new Date(iso).getTime() : NaN
  return Number.isNaN(t) ? -Infinity : t
}

/**
 * Prioridad (Alta → Baja → sin prioridad) y, a igual prioridad, la más nueva
 * primero. El rango se calcula para cada incidencia antes de ordenar, no
 * dentro del comparador: `sort` no llama al comparador en un grupo de una
 * sola, y ese es justo el caso de una estación con una incidencia, donde una
 * prioridad desconocida pasaría sin aviso.
 */
function sortByPriority(list: Incident[]): Incident[] {
  return list
    .map((incident) => ({ incident, rank: priorityRank(incident), at: time(incident.createdAt) }))
    .sort((a, b) => a.rank - b.rank || b.at - a.at)
    .map(({ incident }) => incident)
}

/**
 * Agrupa por `status2` (`tipo_ut_nivel_3`) y ordena cada grupo por prioridad.
 *
 * Ninguna incidencia se descarta: sin `status2` cae en "Sin clasificar", y con
 * un `status2` cuya propiedad no llegó en el lote cae en un grupo propio que
 * lo dice, en vez de desaparecer o de mezclarse con las no clasificadas.
 *
 * Grupos en orden alfabético, estable entre recargas; los que no tienen
 * nombre van al final.
 */
export function groupIncidents(incidents: Incident[]): IncidentGroup[] {
  const byKey = new Map<string, IncidentGroup & { tier: number }>()

  for (const incident of incidents) {
    const { _id, display } = incident.area
    const resolved = display !== '—'
    const key = _id ?? NO_AREA_KEY

    let group = byKey.get(key)
    if (!group) {
      group = !_id
        ? { key, label: NO_AREA_LABEL, tier: 2, incidents: [] }
        : resolved
          ? { key, label: display, tier: 0, incidents: [] }
          : { key, label: `Clasificación no disponible (…${_id.slice(-6)})`, tier: 1, incidents: [] }
      byKey.set(key, group)
    }
    group.incidents.push(incident)
  }

  return [...byKey.values()]
    .sort((a, b) => a.tier - b.tier || a.label.localeCompare(b.label, 'es-CL'))
    .map(({ key, label, incidents: list }) => ({ key, label, incidents: sortByPriority(list) }))
}
